import { useQueries, useQuery } from "@tanstack/react-query";

import type {
  KpiTicketEingrenzung,
  KpiTicketKunden,
  KpiTicketLeads,
  KpiTicketLiegezeit,
  KpiTicketListe,
  KpiTicketMonate,
  KpiTicketQuelle,
  KpiTicketSla,
  KpiTicketWiedereroeffnung,
  TicketZeitraum,
  Vorgangstyp,
} from "@msc/shared";

import { api } from "../lib/api.js";

/** Eingrenzung auf Lead bzw. Beauftragung (Lead-Detail); engt serverseitig nur ein. */
export type TicketEingrenzung = { [K in keyof KpiTicketEingrenzung]?: KpiTicketEingrenzung[K] | undefined };

/** Der Spiegel läuft alle 15 Minuten — öfter zu fragen bringt nichts. */
const FRISCH_MS = 5 * 60_000;

export function ticketParameter(werte: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(werte)) if (v != null) p.set(k, String(v));
  return p.toString();
}

function block<T>(pfad: string, segmentId: string | null, werte: Record<string, string | number | null | undefined>) {
  return {
    queryKey: ["kpi", "tickets", pfad, segmentId, werte],
    queryFn: () => api.get<T>(`/kpi/segments/${segmentId}/tickets/${pfad}?${ticketParameter(werte)}`),
    enabled: segmentId !== null,
    staleTime: FRISCH_MS,
  };
}

export const useKpiTicketMonate = (segmentId: string | null, year: number, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketMonate>(block<KpiTicketMonate>("monate", segmentId, { year, vorgangstyp, ...eingrenzung }));

export const useKpiTicketKunden = (segmentId: string | null, year: number, zeitraum: TicketZeitraum | null, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketKunden>(block<KpiTicketKunden>("kunden", segmentId, { year, zeitraum, vorgangstyp, ...eingrenzung }));

export const useKpiTicketSla = (segmentId: string | null, year: number, zeitraum: TicketZeitraum | null, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketSla>(block<KpiTicketSla>("sla", segmentId, { year, zeitraum, vorgangstyp, ...eingrenzung }));

export const useKpiTicketWiedereroeffnung = (segmentId: string | null, year: number, zeitraum: TicketZeitraum | null, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketWiedereroeffnung>(block<KpiTicketWiedereroeffnung>("wiedereroeffnung", segmentId, { year, zeitraum, vorgangstyp, ...eingrenzung }));

export const useKpiTicketLeads = (segmentId: string | null, zeitraum: TicketZeitraum | null, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketLeads>(block<KpiTicketLeads>("leads", segmentId, { zeitraum, vorgangstyp, ...eingrenzung }));

export const useKpiTicketLiegezeit = (segmentId: string | null, zeitraum: TicketZeitraum | null, vorgangstyp: Vorgangstyp | null, eingrenzung: TicketEingrenzung = {}) =>
  useQuery<KpiTicketLiegezeit>(block<KpiTicketLiegezeit>("liegezeit", segmentId, { zeitraum, vorgangstyp, ...eingrenzung }));

export interface ListenAbfrage {
  quelle: KpiTicketQuelle;
  von: string;
  bis: string;
  vorgangstyp: Vorgangstyp | null;
  abteilungId?: string;
  leadId?: string;
  beauftragungId?: string;
}

/** Die Listen hinter einem Klick, je Abschnitt eine Abfrage; lädt erst bei Auswahl. */
export function useKpiTicketListen(segmentId: string | null, abfragen: ListenAbfrage[] | null) {
  return useQueries({
    queries: (abfragen ?? []).map((a) => ({
      ...block<KpiTicketListe>("liste", segmentId, { ...a }),
      enabled: segmentId !== null && abfragen !== null,
    })),
  });
}
